import { useEffect, useRef, useState } from 'react';
import Supercluster from 'supercluster';
import { BBox, GeoJsonProperties } from 'geojson';
import { isEqual } from 'lodash';

export interface UseSuperclusterArgument<P, C> {
  points: Array<Supercluster.PointFeature<P>>;
  bounds?: BBox;
  zoom: number;
  options?: Supercluster.Options<P, C>;
}

export function useSupercluster<
  P extends GeoJsonProperties = Supercluster.AnyProps,
  C extends GeoJsonProperties = Supercluster.AnyProps,
>({
  points,
  bounds,
  zoom,
  options,
}: UseSuperclusterArgument<P, C>): {
  clusters: Array<Supercluster.ClusterFeature<C> | Supercluster.PointFeature<P>>;
  supercluster: Supercluster<P, C> | undefined;
} {
  const superclusterRef = useRef<Supercluster<P, C>>();
  const pointsRef = useRef<Array<Supercluster.PointFeature<P>>>();
  const [clusters, setClusters] = useState<Array<Supercluster.ClusterFeature<C> | Supercluster.PointFeature<P>>>([]);
  const zoomInt = Math.round(zoom);

  useEffect(() => {
    if (
      !superclusterRef.current ||
      !isEqual(pointsRef.current, points) ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- The options type is not exported
      !isEqual((superclusterRef.current as any).options, options)
    ) {
      superclusterRef.current = new Supercluster(options);
      superclusterRef.current.load(points);
    }

    if (bounds) {
      setClusters(superclusterRef.current.getClusters(bounds, zoomInt));
    }

    pointsRef.current = points;
  }, [points, bounds, zoomInt, options]);

  return { clusters, supercluster: superclusterRef.current };
}

export default useSupercluster;
